import React, { useContext, useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Box, Drawer, AppBar, Toolbar, Typography, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Avatar, IconButton, Chip } from '@mui/material';
import { Dashboard, People, EventNote, CalendarMonth, PhoneCallback, Group, Settings, Logout, Menu as MenuIcon, Storage, Assessment, Summarize, SolarPower } from '@mui/icons-material';
import { AuthContext } from '../../context/AuthContext';
import CallButton from '../CallButton/CallButton';

const W = 280;
const RC = { admin: '#005596', telecaller: '#1b60a2', salesperson: '#00462e' };
const NAV = [
  { text: 'Dashboard',       icon: <Dashboard/>,     path: '/',              roles: ['superadmin','admin','telecaller','salesperson'] },
  { text: 'Pre-Leads',       icon: <People/>,         path: '/preleads',      roles: ['superadmin','admin','telecaller','salesperson'] },
  { text: 'Calendar',        icon: <CalendarMonth/>,  path: '/calendar',      roles: ['superadmin','admin','telecaller','salesperson'] },
  { text: 'Appointments',    icon: <EventNote/>,      path: '/appointments',  roles: ['superadmin','admin','telecaller','salesperson'] },
  { text: 'Recall Queue',    icon: <PhoneCallback/>,  path: '/recalls',       roles: ['superadmin','admin','telecaller','salesperson'] },
  { text: 'Team Management', icon: <Group/>,          path: '/team',          roles: ['superadmin','admin'] },
  { text: 'Master Data',     icon: <Storage/>,        path: '/master-data',   roles: ['superadmin','admin','telecaller'] },
  { text: 'Call Report',     icon: <Assessment/>,     path: '/reports/calls', roles: ['superadmin','admin','telecaller'] },
  { text: 'Solar RGF — New', icon: <SolarPower/>,     path: '/solar-rgf',     roles: ['superadmin','admin','salesperson'] },
  { text: 'Solar RGF — List',icon: <SolarPower/>,     path: '/solar-rgf-list', roles: ['superadmin','admin','salesperson'] },
  { text: 'Reports',         icon: <Summarize/>,      path: '/reports',       roles: ['superadmin','admin','telecaller','salesperson'] },
  { text: 'Settings',        icon: <Settings/>,       path: '/settings',      roles: ['superadmin','admin','telecaller','salesperson'] },
];

function useBrandFonts() {
  useEffect(() => {
    if (document.getElementById('anvil-brand-fonts')) return;
    const link = document.createElement('link');
    link.id = 'anvil-brand-fonts';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap';
    document.head.appendChild(link);
  }, []);
}

function HeliosBackground() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let CW, CH;
    const NODES = [];
    const COUNT = 40;

    const resize = () => {
      CW = canvas.offsetWidth; CH = canvas.offsetHeight;
      canvas.width = CW; canvas.height = CH;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < COUNT; i++) {
      NODES.push({
        x: Math.random() * (CW || 900), y: Math.random() * (CH || 700),
        vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 2 + 1, pulse: Math.random() * Math.PI * 2,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, CW, CH);

      // subtle grid
      ctx.strokeStyle = 'rgba(27,96,162,0.05)';
      ctx.lineWidth = 1;
      for (let x = 0; x < CW; x += 56) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,CH); ctx.stroke(); }
      for (let y = 0; y < CH; y += 56) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(CW,y); ctx.stroke(); }

      // connections
      for (let i = 0; i < NODES.length; i++) {
        for (let j = i+1; j < NODES.length; j++) {
          const dx = NODES[i].x - NODES[j].x, dy = NODES[i].y - NODES[j].y;
          const dist = Math.sqrt(dx*dx + dy*dy);
          if (dist < 130) {
            ctx.beginPath(); ctx.moveTo(NODES[i].x, NODES[i].y); ctx.lineTo(NODES[j].x, NODES[j].y);
            ctx.strokeStyle = `rgba(0,85,150,${0.08 * (1 - dist/130)})`;
            ctx.lineWidth = 0.6; ctx.stroke();
          }
        }
      }

      // nodes
      NODES.forEach(n => {
        n.pulse += 0.018;
        const alpha = 0.15 + 0.1 * Math.sin(n.pulse);
        ctx.beginPath(); ctx.arc(n.x, n.y, n.r + Math.sin(n.pulse)*0.5, 0, Math.PI*2);
        ctx.fillStyle = `rgba(27,96,162,${alpha})`; ctx.fill();
        n.x += n.vx; n.y += n.vy;
        if (n.x < 0 || n.x > CW) n.vx *= -1;
        if (n.y < 0 || n.y > CH) n.vy *= -1;
      });

      // solar orbs — blue + gold
      const t = Date.now() * 0.001;
      [
        { cx: CW*0.8,  cy: CH*0.15, r: 80,  color: '0,62,111',   opacity: 0.06 },
        { cx: CW*0.15, cy: CH*0.8,  r: 60,  color: '252,184,18', opacity: 0.08 },
        { cx: CW*0.9,  cy: CH*0.8,  r: 50,  color: '0,96,65',    opacity: 0.05 },
      ].forEach(o => {
        const r = o.r + Math.sin(t * 0.7) * 10;
        const g = ctx.createRadialGradient(o.cx, o.cy, 0, o.cx, o.cy, r);
        g.addColorStop(0,   `rgba(${o.color},${o.opacity})`);
        g.addColorStop(0.5, `rgba(${o.color},${o.opacity * 0.5})`);
        g.addColorStop(1,   `rgba(${o.color},0)`);
        ctx.beginPath(); ctx.arc(o.cx, o.cy, r, 0, Math.PI*2);
        ctx.fillStyle = g; ctx.fill();
        ctx.beginPath(); ctx.arc(o.cx, o.cy, r*0.3, 0, Math.PI*2);
        ctx.strokeStyle = `rgba(${o.color},${o.opacity * 1.5})`; ctx.lineWidth = 1; ctx.stroke();
      });

      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  return <canvas ref={canvasRef} style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', pointerEvents:'none', zIndex:0 }} />;
}

export default function Layout() {
  useBrandFonts();
  const { user, logout, permissions: ctxPermissions } = useContext(AuthContext);
  // Get permissions for current user's role from allPermissions store
  const permissions = (() => {
    try {
      const all = JSON.parse(localStorage.getItem('allPermissions') || '{}');
      const rolePerms = all[user?.role];
      if (rolePerms && Object.keys(rolePerms).length > 0) return rolePerms;
    } catch(e) {}
    return ctxPermissions || {};
  })();
  const navigate = useNavigate();
  const location = useLocation();
  const [mob, setMob] = useState(false);
  const isSuperAdmin = user?.role === 'superadmin';
  const isAdmin = user?.role === 'admin' || isSuperAdmin;

  const nav = NAV.filter(i => {
    if (!i.roles.includes(user?.role)) return false;
    if (isAdmin) return true; // admin/superadmin always sees all nav
    // Map path to permission module key
    const pathKey = i.path.replace('/','').replace(/-/g,'').split('/')[0] || 'dashboard';
    const keyMap = {
      '': 'dashboard', 'dashboard': 'dashboard',
      'preleads': 'preleads', 'calendar': 'calendar',
      'appointments': 'appointments', 'recalls': 'recalls',
      'team': 'team', 'masterdata': 'masterdata',
      'reportscalls': 'callreport', 'reports': 'reports', 'solarrgf': 'solarrgf', 'solarrgflist': 'solarrgf', 'settings': 'settings',
    };
    const modKey = keyMap[pathKey] || pathKey;
    const permsLoaded = permissions && Object.keys(permissions).length > 0;
    if (!permsLoaded) return true; // permissions not loaded yet — show all temporarily
    const mod = permissions[modKey];
    if (mod === undefined) return false; // module not in permissions = deny
    return mod.view === true;
  });
  const handleLogout = () => { logout(); navigate('/login'); };

  const drawer = (
    <Box sx={{ height:'100%', display:'flex', flexDirection:'column', bgcolor:'#0B1526' }}>
      <Box sx={{ p:2, display:'flex', alignItems:'center', justifyContent:'center', minHeight:68, borderBottom:'1px solid rgba(255,255,255,0.1)' }}>
        <img src="/anvil-logo.png" alt="Anvil Energy" style={{ height:40, objectFit:'contain', maxWidth:200 }} onError={e=>{e.target.style.display='none';}}/>
      </Box>
      <Box sx={{ px:2, py:1.5, borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
        <Typography sx={{ color:'rgba(255,255,255,0.45)', fontSize:9, letterSpacing:1.2, display:'block', mb:0.3, textTransform:'uppercase' }}>Company</Typography>
        <Typography variant="body2" fontWeight={700} sx={{ color:'white', fontSize:13 }}>Anvil Energy Pvt. Ltd</Typography>
        <Typography sx={{ color:'rgba(255,255,255,0.4)', fontSize:10 }}>TeleConnect Pro</Typography>
      </Box>
      <Box sx={{ px:2, py:1.5, borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar sx={{ bgcolor: RC[user?.role]||'#005596', width:36, height:36, fontSize:14, fontWeight:700, border:'2px solid rgba(11,99,201,0.5)' }}>
            {user?.name?.charAt(0)?.toUpperCase()||'U'}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ color:'white', lineHeight:1.4, fontSize:13 }}>{user?.name||user?.email||'User'}</Typography>
            <Chip label={user?.role||'user'} size="small" sx={{ bgcolor:'rgba(11,99,201,0.2)', color:'#0B63C9', fontSize:10, height:17, mt:0.2 }}/>
          </Box>
        </Box>
      </Box>
      <Box sx={{ flex:1, overflow:'auto', py:1 }}>
        <List dense disablePadding>
          {nav.map(item => {
            const on = location.pathname === item.path;
            return (
              <ListItem key={item.text} disablePadding sx={{ px:1, mb:0.3 }}>
                <ListItemButton onClick={()=>{ navigate(item.path); setMob(false); }}
                  sx={{ borderRadius:1, bgcolor: on ? 'rgba(11,99,201,0.15)' : 'transparent', '&:hover':{ bgcolor:'rgba(255,255,255,0.08)' }, py:0.9, px:1.5 }}>
                  <ListItemIcon sx={{ color: on ? '#0B63C9' : 'rgba(255,255,255,0.6)', minWidth:34 }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} primaryTypographyProps={{ fontSize:13, fontWeight: on?600:400, color: on?'white':'rgba(255,255,255,0.7)' }}/>
                  {on && <Box sx={{ width:3, height:18, bgcolor:'#0B63C9', borderRadius:2, ml:1 }}/>}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
      <Box sx={{ p:1, borderTop:'1px solid rgba(255,255,255,0.08)' }}>
        <ListItemButton onClick={handleLogout} sx={{ borderRadius:1, '&:hover':{ bgcolor:'rgba(186,26,26,0.15)' }, py:0.9, px:1.5 }}>
          <ListItemIcon sx={{ color:'rgba(255,255,255,0.45)', minWidth:34 }}><Logout fontSize="small"/></ListItemIcon>
          <ListItemText primary="Logout" primaryTypographyProps={{ fontSize:13, color:'rgba(255,255,255,0.55)' }}/>
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display:'flex', minHeight:'100vh' }}>
      <Box component="nav" sx={{ width:{ md:W }, flexShrink:{ md:0 } }}>
        <Drawer variant="temporary" open={mob} onClose={()=>setMob(false)} ModalProps={{ keepMounted:true }}
          sx={{ display:{ xs:'block', md:'none' }, '& .MuiDrawer-paper':{ width:W, boxSizing:'border-box' } }}>
          {drawer}
        </Drawer>
        <Drawer variant="permanent"
          sx={{ display:{ xs:'none', md:'block' }, '& .MuiDrawer-paper':{ width:W, boxSizing:'border-box', border:'none', boxShadow:'4px 0 20px rgba(0,62,111,0.15)' } }}>
          {drawer}
        </Drawer>
      </Box>
      <Box sx={{ flexGrow:1, display:'flex', flexDirection:'column', minHeight:'100vh', position:'relative', bgcolor:'#f0f3ff' }}>
        <HeliosBackground/>
        <AppBar position="static" elevation={0} sx={{ display:{ md:'none' }, bgcolor:'#0B1526', position:'relative', zIndex:1 }}>
          <Toolbar sx={{ gap:1 }}>
            <IconButton color="inherit" onClick={()=>setMob(true)}><MenuIcon/></IconButton>
            <Typography variant="subtitle2" fontWeight={700}>Anvil Energy</Typography>
          </Toolbar>
        </AppBar>
        <Box sx={{ flex:1, overflow:'auto', position:'relative', zIndex:1 }}>
          <Outlet/>
        </Box>
      </Box>
    </Box>
  );
}
